import { useState } from "react";

// On-chain references — real deployed values
export const PROGRAMS = {
  identity: "AkKx54ZmuP17r1sXsKr7mxe3dXJ5RMqsSH2zf8QGZ39C",
  swig: "swigypWHEksbC64pWKwah1WTeh9JXwx8H1rJHLdbQMB",
  genesisDeployTx:
    "57jLw4fMTbMsaxxKxjxfPAQe34PE7oQZRFZnun5RsabZjbLLS3xwaW4yjL8qD2H7cj8UDMy3mayJTuKzhuMYvKHm",
  operator: "5TLRa7qHxFTAuY3XDoPpjEmSfqv37ZBykwuG6evfG8vU",
} as const;

const CLUSTER = (import.meta.env.VITE_SOLANA_CLUSTER ?? "devnet") as string;

export const SOLSCAN = (addr: string) =>
  `https://solscan.io/account/${addr}?cluster=${CLUSTER}`;
export const SOLSCAN_TX = (sig: string) =>
  `https://solscan.io/tx/${sig}?cluster=${CLUSTER}`;

export function truncate(addr: string, head = 4, tail = 4): string {
  if (!addr) return "";
  if (addr.length <= head + tail + 1) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

interface TruncatedAddrProps {
  addr: string;
  href?: string;
  label?: string;
  copyable?: boolean;
}

export function TruncatedAddr({ addr, href, label, copyable = true }: TruncatedAddrProps) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!copyable) return;
    navigator.clipboard?.writeText(addr).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <span
      className={`addr ${revealed ? "addr--revealed" : ""}`}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={() => setRevealed(false)}
      tabIndex={0}
    >
      {label && <span className="addr__label">{label}</span>}
      <span className="addr__mono">
        <span className="addr__short" aria-hidden={revealed}>
          {truncate(addr)}
        </span>
        <span className="addr__full" aria-hidden={!revealed}>
          {addr}
        </span>
      </span>
      {href && (
        <a
          className="addr__link"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label="Open on Solscan"
        >
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
            <path d="M2 1 H 8 V 7" stroke="currentColor" strokeWidth="1" />
            <path d="M 8 1 L 1 8" stroke="currentColor" strokeWidth="1" />
          </svg>
        </a>
      )}
      {copyable && (
        <button
          className="addr__copy"
          onClick={handleCopy}
          aria-label="Copy address"
          title={copied ? "copied" : "copy"}
        >
          {copied ? (
            "✓"
          ) : (
            <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
              <rect x="1" y="1" width="5" height="5" stroke="currentColor" strokeWidth="0.8" fill="none" />
              <rect x="3" y="3" width="5" height="5" stroke="currentColor" strokeWidth="0.8" fill="none" />
            </svg>
          )}
        </button>
      )}
    </span>
  );
}

interface RingFillProps {
  pct: number;
  size?: number;
  label?: string;
  sub?: string;
  tone?: "bone" | "bronze";
}

export function RingFill({ pct, size = 64, tone = "bone" }: RingFillProps) {
  const r = 28;
  const c = 2 * Math.PI * r;
  const fill = Math.min(Math.max(pct, 0), 1);
  const dashOffset = c * (1 - fill);
  const cap = pct >= 0.85;
  const stroke = cap ? "var(--oxblood)" : tone === "bronze" ? "var(--bronze)" : "var(--bone)";
  return (
    <div className="ring">
      <svg width={size} height={size} viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="var(--line)" strokeWidth="1.4" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="1.6"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 32 32)"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          return (
            <line
              key={i}
              x1={32 + Math.cos(a) * 22}
              y1={32 + Math.sin(a) * 22}
              x2={32 + Math.cos(a) * 24}
              y2={32 + Math.sin(a) * 24}
              stroke="var(--line)"
              strokeWidth="0.8"
            />
          );
        })}
      </svg>
      <div className="ring__center">
        <div className="ring__pct">
          {Math.round(fill * 100)}
          <span>%</span>
        </div>
      </div>
    </div>
  );
}

export function fmtRelTime(min: number): string {
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ${min % 60}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h ago`;
}

export function fmtAmount(n: number, mint: string): string {
  if (mint === "USDC")
    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
